/**
 * ヘッダー HUD（タイトル / スコア / ハイスコア / コンボ / 一時停止・設定ボタン）
 * と NEXT 表示を担当する。
 */

import { regionShortName } from '../game/config/types';
import { GameState } from '../core/GameState';
import type { GameContext } from '../game/GameContext';
import { confirmDialog } from './confirmDialog';

function query<T extends HTMLElement>(root: ParentNode, name: string): T | null {
  return root.querySelector<T>(`[data-ui="${name}"]`);
}

/**
 * サブタイトルの既定値。
 *
 * 品数はステージの items から数えるので、県ごとに段数が違っても正しく出る。
 * ステージ側で subtitle を指定した場合はそちらが優先される。
 */
function defaultSubtitle(context: GameContext): string {
  const count = context.items.length;
  switch (context.stage.mode) {
    case 'sightseeing':
      return `名所 全${count}か所`;
    case 'person':
      return `人物 全${count}人`;
    default:
      return `名物めし 全${count}品`;
  }
}

export class HudController {
  private readonly context: GameContext;
  private readonly root: HTMLElement;
  private currentScore = 0;
  /** このプレイで盤面に出た最高レベル（0 ならまだ何も落としていない）。 */
  private runMaxLevel = 0;
  private confirming = false;
  /**
   * 盤面の右上に重ねる小さな NEXT。
   * スマホでは側面パネルの NEXT が画面の下に押し出されて見えず、先読みができなかった。
   */
  private readonly miniNextIcon: HTMLImageElement = HudController.mountMiniNext();

  private static mountMiniNext(): HTMLImageElement {
    const box = document.createElement('div');
    box.className = 'mini-next';
    box.setAttribute('aria-hidden', 'true');
    const label = document.createElement('span');
    label.className = 'mini-next__label';
    label.textContent = 'NEXT';
    const img = document.createElement('img');
    img.className = 'mini-next__icon';
    img.width = 40;
    img.height = 40;
    img.alt = '';
    box.append(label, img);
    document.querySelector('.stage-frame')?.append(box);
    return img;
  }

  private readonly scoreEl: HTMLElement | null;
  private readonly highScoreEl: HTMLElement | null;
  private readonly comboEl: HTMLElement | null;
  private readonly comboBox: HTMLElement | null;
  private readonly pauseButton: HTMLButtonElement | null;
  private readonly pauseLabel: HTMLElement | null;
  private readonly currentIcon: HTMLImageElement | null;
  private readonly currentName: HTMLElement | null;
  private readonly nextIcon: HTMLImageElement | null;
  private readonly nextName: HTMLElement | null;

  constructor(context: GameContext, root: HTMLElement = document.body) {
    this.context = context;
    this.root = root;

    this.scoreEl = query(root, 'score');
    this.highScoreEl = query(root, 'high-score');
    this.comboEl = query(root, 'combo');
    this.comboBox = query(root, 'combo-box');
    this.pauseButton = query<HTMLButtonElement>(root, 'pause');
    this.pauseLabel = query(root, 'pause-label');
    this.currentIcon = query<HTMLImageElement>(root, 'current-icon');
    this.currentName = query(root, 'current-name');
    this.nextIcon = query<HTMLImageElement>(root, 'next-icon');
    this.nextName = query(root, 'next-name');

    this.applyStageText();
    this.bindButtons();
    this.bindEvents();
    this.setHighScore(context.storage.highScore);
  }

  private applyStageText(): void {
    const titleEl = query(this.root, 'title');
    const subtitleEl = query(this.root, 'subtitle');
    const footerEl = query(this.root, 'footer-note');
    const disclaimerEl = query(this.root, 'footer-disclaimer');
    // 背景ツアーの呼び名は県ごとに変わる（「福岡めぐり」「北海道めぐり」）。
    // index.html に直接書くと、県を足したときに福岡のまま残る。
    const tourName = `${regionShortName(this.context.stage.regionName)}めぐり`;
    const tourTitleEl = query(this.root, 'tour-title');
    const tourPanelEl = query(this.root, 'tour-panel');
    const tourResetEl = query(this.root, 'tour-reset');
    if (tourTitleEl) tourTitleEl.textContent = tourName;
    if (tourPanelEl) tourPanelEl.setAttribute('aria-label', tourName);
    if (tourResetEl) {
      tourResetEl.textContent = `${tourName}を最初から`;
      tourResetEl.setAttribute('aria-label', `${tourName}の旅を最初からやり直す`);
    }
    if (titleEl) titleEl.textContent = this.context.stage.title;
    if (subtitleEl) {
      subtitleEl.textContent = this.context.stage.subtitle ?? defaultSubtitle(this.context);
    }
    if (footerEl) {
      footerEl.textContent = `${this.context.stage.title} — ${this.context.stage.regionName}の名物めしを合体させて進化させよう`;
    }
    if (disclaimerEl) {
      // 記者クラブ・観光団体への露出で「公的なもの」と誤解されないよう明示する
      disclaimerEl.textContent = `本作は個人が制作した非公式のゲームです。${this.context.stage.regionName}および掲載する施設・団体の公認・監修を受けたものではありません。`;
    }
    document.title = this.context.stage.title;
  }

  private bindButtons(): void {
    const bus = this.context.bus;

    this.pauseButton?.addEventListener('click', () => bus.emit('command:togglePause'));
    // 途中で押すと盤面が消えるので、遊んでいる最中だけ確認する（おはしの隣にあり誤タップしやすい）
    query<HTMLButtonElement>(this.root, 'restart')?.addEventListener('click', () => {
      bus.emit('command:requestRestart');
    });
    // R キーや一時停止画面の「リスタート」も同じ確認を通す（以前はボタンだけ確認していた）
    bus.on('command:requestRestart', async () => {
      if (this.confirming) return;
      const state = this.context.state;
      // 1つでも落としていれば「途中」。合体する前（0点）でも盤面は消える
      const inProgress =
        state.is(GameState.PLAYING, GameState.PAUSED) && (this.currentScore > 0 || this.runMaxLevel > 0);
      if (!inProgress) {
        bus.emit('command:restart');
        return;
      }
      const wasPlaying = state.is(GameState.PLAYING);
      if (wasPlaying) bus.emit('command:pause');
      this.confirming = true;
      const ok = await confirmDialog({
        title: '最初からやり直しますか？',
        body: 'いまの盤面とスコアは消えます。',
        okLabel: 'やり直す',
        cancelLabel: 'つづける',
      }).finally(() => {
        this.confirming = false;
      });
      if (ok) bus.emit('command:restart');
      else if (wasPlaying) bus.emit('command:resume');
    });
  }

  private bindEvents(): void {
    const bus = this.context.bus;

    bus.on('run:maxLevel', ({ level }) => {
      this.runMaxLevel = level;
    });
    bus.on('score:change', ({ score }) => {
      this.currentScore = score;
      this.setScore(score);
    });
    bus.on('highscore:change', ({ highScore }) => this.setHighScore(highScore));
    bus.on('combo:change', ({ combo }) => this.setCombo(combo));
    bus.on('queue:change', ({ current, next }) => {
      if (this.currentIcon) {
        this.currentIcon.src = this.context.icons.iconUrl(current, 128);
        this.currentIcon.alt = `いま落とす料理: レベル${current.level} ${current.name}`;
      }
      if (this.currentName) this.currentName.textContent = current.name;
      if (this.nextIcon) {
        this.nextIcon.src = this.context.icons.iconUrl(next, 128);
        this.nextIcon.alt = `次の料理: レベル${next.level} ${next.name}`;
      }
      if (this.nextName) this.nextName.textContent = next.name;
      this.miniNextIcon.src = this.context.icons.iconUrl(next, 96);
      this.miniNextIcon.alt = `次の料理: ${next.name}`;
    });

    this.context.state.onChange((to) => this.applyState(to));
    this.applyState(this.context.state.state);
  }

  private applyState(state: string): void {
    const paused = state === GameState.PAUSED;
    if (this.pauseLabel) this.pauseLabel.textContent = paused ? '▶' : '⏸';
    if (this.pauseButton) {
      this.pauseButton.setAttribute('aria-label', paused ? 'ゲームを再開する' : '一時停止する');
      this.pauseButton.disabled = state === GameState.LOADING || state === GameState.GAME_OVER;
    }
    // 終わったあとまで「コンボ 5」が光ったまま残らないように
    if (state === GameState.GAME_OVER) this.setCombo(0);
  }

  setScore(score: number): void {
    if (this.scoreEl) this.scoreEl.textContent = score.toLocaleString('ja-JP');
  }

  setHighScore(score: number): void {
    if (this.highScoreEl) this.highScoreEl.textContent = score.toLocaleString('ja-JP');
  }

  setCombo(combo: number): void {
    if (this.comboEl) this.comboEl.textContent = String(combo);
    if (this.comboBox) this.comboBox.dataset.active = combo >= 2 ? 'true' : 'false';
  }
}
